import { Icons } from "@win-reality/react-ui";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useClickAway } from "react-use";

import { cn } from "@/lib/utils";
import type { RouteMenuItem } from "@/types/routes";

interface ContextMenuProps {
  items?: RouteMenuItem[];
  className?: string;
}

/**
 * Context menu component (ellipsis button) for navbar.
 * Renders menu items from route handle configuration.
 */
const ContextMenu: React.FC<ContextMenuProps> = ({ items, className }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  useClickAway(dropdownRef, () => setIsOpen(false));

  // Don't render if no menu items
  if (!items || items.length === 0) {
    return null;
  }

  const handleItemClick = (item: RouteMenuItem) => {
    setIsOpen(false);

    switch (item.action.type) {
      case "navigate":
        navigate(item.action.to);
        break;
      case "callback":
        item.action.callback();
        break;
      case "modal":
        // TODO: Implement modal trigger system
        console.log(`Open modal: ${item.action.modal}`);
        break;
    }
  };

  return (
    <div className={cn("relative", className)} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center justify-center",
          "size-10 rounded-full",
          "hover:bg-gray-200",
          "transition-colors duration-150",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        )}
        aria-label="Menu"
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        <Icons.EllipsisVertical className="size-5 text-gray-700" />
      </button>

      {isOpen && (
        <div
          className="absolute right-0 top-full mt-2 w-48 rounded-md border border-gray-200 bg-white py-1 shadow-lg z-50"
          role="menu"
        >
          {items.map((item) => {
            const IconComponent = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => handleItemClick(item)}
                className={cn(
                  "flex w-full items-center gap-2 px-4 py-2 text-sm",
                  "hover:bg-gray-100 transition-colors",
                  item.destructive
                    ? "text-red-600 hover:text-red-700"
                    : "text-gray-700 hover:text-gray-900",
                )}
                role="menuitem"
              >
                {IconComponent && <IconComponent className="size-4" />}
                {item.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ContextMenu;
